import { IDropdownOption } from '@app/core/models/configurable-components.model'
import { generateAcademicYearOptions } from '../helpers/academic-year.helper'

export function getClientLeadListAcademicYearOptions(): IDropdownOption[] {
    return [
        { value: '', label: 'All' },
        ...generateAcademicYearOptions().map((year) => ({ value: year, label: year })),
    ]
}

export const CLIENT_LEAD_LIST_STAGE_OPTIONS: IDropdownOption[] = [
    { value: '', label: 'All' },
    { value: 'S0', label: 'S0 — All Schools' },
    { value: 'S1', label: 'S1 — Prospecting' },
    { value: 'S2', label: 'S2 — Prospect Identified' },
    { value: 'S3', label: 'S3 — Demo Completed' },
    { value: 'S4', label: 'S4 — Proposal Submitted' },
    { value: 'S5', label: 'S5 — Contract Negotiation' },
    { value: 'S6', label: 'S6 — SSF Signed' },
    { value: 'S7', label: 'S7 — Deal Won' },
]

export const CLIENT_LEAD_LIST_STATUS_OPTIONS: IDropdownOption[] = [
    { value: '', label: 'All' },
    { value: 'Lead Created', label: 'Lead Created' },
    { value: 'Proposal Sent to Client', label: 'Proposal Sent to Client' },
    { value: 'Lead Dropped', label: 'Lead Dropped' },
    { value: 'Lead Contacted', label: 'Lead Contacted' },
]

export const CLIENT_LEAD_LIST_STAGE_BADGE_CLASSES = {
    S0: 'lead-row__stage-badge--s0',
    S1: 'lead-row__stage-badge--s1',
    S2: 'lead-row__stage-badge--s2',
    S3: 'lead-row__stage-badge--s3',
    S4: 'lead-row__stage-badge--s4',
    S5: 'lead-row__stage-badge--s5',
    S6: 'lead-row__stage-badge--s6',
    S7: 'lead-row__stage-badge--s7',
    S8: 'lead-row__stage-badge--s8',
    S9: 'lead-row__stage-badge--s9',
} as const

export const CLIENT_LEAD_LIST_DEFAULT_STAGE_BADGE_CLASS = 'lead-row__stage-badge--default' as const

export const CLIENT_LEAD_LIST_STATUS_TYPE_VALUES = {
    ACTIVE: 'open',
    LOST: 'dropped',
    DEFERRED: 'on_hold',
} as const

export const CLIENT_LEAD_LIST_STATUS_TYPE_OPTIONS: IDropdownOption[] = [
    { value: '', label: 'All' },
    { value: CLIENT_LEAD_LIST_STATUS_TYPE_VALUES.ACTIVE, label: 'Active' },
    { value: CLIENT_LEAD_LIST_STATUS_TYPE_VALUES.LOST, label: 'Lost' },
    { value: CLIENT_LEAD_LIST_STATUS_TYPE_VALUES.DEFERRED, label: 'Deferred' },
]

export const CLIENT_LEAD_LIST_STATUS_LABELS = {
    LEAD_DROPPED: 'Lead Dropped',
} as const

export const CLIENT_LEAD_LIST_TABLE_COLUMN_LABELS = {
    LEAD_OWNER: 'Lead Owner',
} as const

export const CLIENT_LEAD_LIST_TABLE_ACTION_KEYS = {
    RECORD_COMMUNICATION: 'recordCommunication',
    MOVE_TO_S1: 'moveToS1',
    VIEW_LEAD: 'viewLead',
} as const

// Create Lead Modal Constants
export const CREATE_LEAD_MODAL_CONSTANTS = {
    MODAL_TITLE: 'Create Lead',
    CLOSE_BUTTON_ARIA_LABEL: 'Close',
    CLOSE_ICON_NAME: 'close',
    SUCCESS_MESSAGE: 'Lead created successfully',
    SUCCESS_MESSAGE_DURATION: 3000,
    SNACKBAR_HORIZONTAL_POSITION: 'right' as const,
    SNACKBAR_VERTICAL_POSITION: 'top' as const,
    SNACKBAR_CLOSE_LABEL: 'Close',
    DEFAULT_TIMEOUT: 1000,
} as const

export const CREATE_LEAD_FORM_FIELDS = {
    LEAD_NAME: 'leadName',
    ACADEMIC_YEAR: 'academicYear',
    INTERACTED_PRODUCTS: 'interactedProducts',
    ADDITIONAL_INFORMATION: 'additionalInformation',
} as const

export const CREATE_LEAD_FORM_LABELS = {
    LEAD_NAME: 'Lead Name',
    ACADEMIC_YEAR: 'Academic Year',
    INTERACTED_PRODUCTS: 'Interacted Products',
    ADDITIONAL_INFORMATION: 'Additional Information',
} as const

export const CREATE_LEAD_FORM_PLACEHOLDERS = {
    ADDITIONAL_INFORMATION: 'Enter any additional details about this lead...',
} as const

export const CREATE_LEAD_MODAL_CONSTANTS_WITH_PLACEHOLDER = {
    ...CREATE_LEAD_MODAL_CONSTANTS,
    ADDITIONAL_INFORMATION_PLACEHOLDER: CREATE_LEAD_FORM_PLACEHOLDERS.ADDITIONAL_INFORMATION,
} as const

export const CREATE_LEAD_BUTTON_LABELS = {
    CANCEL: 'Cancel',
    CREATE_LEAD: 'Create Lead',
    CREATING: 'Creating...',
} as const

export const CREATE_LEAD_MODAL_DIALOG_CONFIG = {
    WIDTH: '600px',
    MAX_WIDTH: '90vw',
    MAX_HEIGHT: '90vh',
    DISABLE_CLOSE: false,
} as const

// Client Lead List Component Constants
export const CLIENT_LEAD_LIST_CONSTANTS = {
    DEBOUNCE_TIME_MS: 300,
    MAX_TRUNCATE_LENGTH: 25,
    EMPTY_PRODUCTS_DISPLAY: '-',
    PRODUCTS_PER_LINE: 2,
    MOVE_TO_S1_SUCCESS_TITLE: 'Lead moved from Stage 0 to Stage 1 successfully',
    MOVE_TO_S1_SUCCESS_MESSAGE:
        'The lead has been moved to S1 — Prospecting. You can continue follow-ups from the lead detail view.',
    MOVE_TO_S1_ERROR_TITLE: 'Could not move to S1',
    MOVE_TO_S1_ERROR_FALLBACK: 'Something went wrong. Please try again.',
    MOVE_TO_S1_TOOLTIP_LOADING: 'Checking communication history…',
    MOVE_TO_S1_TOOLTIP_NEED_QUALIFYING_COMM:
        'Before moving to S1, record at least one communication: a connected call, an in-person meeting, or an email.',

    MOVE_TO_S1_TOOLTIP_LEAD_CREATED_NOTE:
        'To create another lead for this client, all existing leads must either reach S6 (SSF Signed) or later, or be marked as lost.',
    STAGE_NAMES: [
        'Prospecting',
        'Prospect Identified',
        'Demo Completed',
        'Proposal Submitted',
        'Contract Negotiation',
        'SSF Signed',
        'Deal Won',
        'Lead Lost',
        'Lead Deferred',
    ] as const,
} as const
